import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { NotificationProvider } from "@/context/NotificationContext";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import OnboardingPage from "@/pages/OnboardingPage";
import PricingPage from "@/pages/PricingPage";
import DashboardPage from "@/pages/DashboardPage";
import ProfileEditPage from "@/pages/ProfileEditPage";
import AdminPage from "@/pages/AdminPage";
import CoAdminDashboardPage from "@/pages/CoAdminDashboardPage";
import EventDetailPage from "@/pages/EventDetailPage";
import CheckInPage from "@/pages/CheckInPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import CommunityBoardPage from "@/pages/CommunityBoardPage";
import MyCityPage from "@/pages/MyCityPage";
import WellnessPage from "@/pages/WellnessPage";
import SettingsPrivacyPage from "@/pages/SettingsPrivacyPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import MessagesPage from "@/pages/MessagesPage";
import CirclePage from "@/pages/CirclePage";

// Admin Pages
import AdminSetupPage from "@/pages/AdminSetupPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminForgotPasswordPage from "@/pages/AdminForgotPasswordPage";
import AdminResetPasswordPage from "@/pages/AdminResetPasswordPage";
import AdminSetupAccountPage from "@/pages/AdminSetupAccountPage";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, initialized, hasToken, refreshUser } = useAuth();
  const location = window.location;
  
  // Check if returning from payment - give extra time for auth
  const isPaymentReturn = location.search.includes('payment=success') || 
                          location.search.includes('session_id=');
  
  // Show loading while auth is initializing
  if (loading || !initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-white">
        <div className="animate-pulse text-teal text-lg">Loading...</div>
      </div>
    );
  }
  
  // If we have a token but no user yet, keep loading (auth might still be fetching)
  if (hasToken && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-white">
        <div className="animate-pulse text-teal text-lg">Loading your profile...</div>
      </div>
    );
  }
  
  // For payment returns, wait a bit longer before redirecting
  // The token should be in localStorage from before the Stripe redirect
  if (isPaymentReturn && !user && !hasToken) {
    // Check localStorage directly one more time
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      // Token exists, trigger a refresh
      refreshUser();
      return (
        <div className="min-h-screen flex items-center justify-center bg-warm-white">
          <div className="animate-pulse text-teal text-lg">Completing your payment...</div>
        </div>
      );
    }
  }
  
  // No token and no user = redirect to login
  if (!user && !hasToken) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && !user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Search-engine exclusion: only public marketing/event/legal pages are
// indexable; every member-facing route gets a noindex robots meta tag.
const INDEXABLE_PATHS = [
  /^\/$/, /^\/pricing/, /^\/events\//, /^\/signup/, /^\/login/,
  /^\/reset-password/, /^\/privacy/, /^\/terms/,
];

function RobotsMeta() {
  const location = useLocation();
  useEffect(() => {
    const isPublic = INDEXABLE_PATHS.some((r) => r.test(location.pathname));
    let tag = document.querySelector('meta[name="robots"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.name = "robots";
      document.head.appendChild(tag);
    }
    tag.content = isPublic ? "index, follow" : "noindex, nofollow, noarchive, noimageindex";
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
      <BrowserRouter>
        <RobotsMeta />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />

          {/* Event detail — PUBLIC (shareable). Non-members see a preview and a signup CTA. */}
          <Route path="/events/:eventId" element={<EventDetailPage />} />
          {/* Door check-in scanner — hosts (admins + event co-admins) only */}
          <Route
            path="/checkin/:eventId"
            element={
              <ProtectedRoute>
                <CheckInPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/onboarding" 
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/profile/edit" 
            element={
              <ProtectedRoute>
                <ProfileEditPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/community" 
            element={
              <ProtectedRoute>
                <CommunityBoardPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/messages" 
            element={
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/my-city" 
            element={
              <ProtectedRoute>
                <MyCityPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/wellness"
            element={
              <ProtectedRoute>
                <WellnessPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/privacy"
            element={
              <ProtectedRoute>
                <SettingsPrivacyPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/circle" 
            element={
              <ProtectedRoute>
                <CirclePage />
              </ProtectedRoute>
            } 
          />
          {/* Admin dashboard - uses member auth with is_admin check */}
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/dashboard" element={<AdminPage />} />

          {/* Co-Admin abbreviated dashboard (members with can_manage_events=true) */}
          <Route
            path="/coadmin/dashboard"
            element={
              <ProtectedRoute>
                <CoAdminDashboardPage />
              </ProtectedRoute>
            }
          />
          
          {/* Admin login redirects to member login */}
          <Route path="/admin/login" element={<Navigate to="/login?redirect=/admin/dashboard" replace />} />
          
          {/* Admin System Routes (Setup still needed for initial bootstrap) */}
          <Route path="/admin/setup" element={<AdminSetupPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
          <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
          <Route path="/admin/setup-account" element={<AdminSetupAccountPage />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
